import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Copy, ExternalLink, Link2, RefreshCw, ShieldOff } from 'lucide-react';
import useClassAccess from '../../hooks/useClassAccess';
import { useAuth } from '../../context/AuthContext';
import { showConfirmDialog, showToast } from '../../utils/feedback';
import Spinner from '../Common/Spinner';
import './ClassAccess.css';

const clean = (value) => String(value || '').trim();

const createClassKey = (student) => {
  const standard = clean(student?.std_roman);
  const division = clean(student?.custom_div || student?.division);
  return `${standard}__${division}`.toLowerCase();
};

const ClassLinkManager = ({ students = [] }) => {
  const { schoolId } = useAuth();
  const { loading, listLinks, generateLink, regenerateLink, disableLink } = useClassAccess();
  const [links, setLinks] = useState([]);
  const [busyKey, setBusyKey] = useState('');

  const classes = useMemo(() => {
    const map = new Map();

    students.forEach((student) => {
      const standard = clean(student?.std_roman);
      if (!standard) return;

      const division = clean(student?.division);
      const customDiv = clean(student?.custom_div);
      const key = createClassKey(student);

      if (!map.has(key)) {
        map.set(key, {
          key,
          std_roman: standard,
          division,
          custom_div: customDiv,
          label: [standard, customDiv || division].filter(Boolean).join(' - '),
          studentCount: 0,
        });
      }

      map.get(key).studentCount += 1;
    });

    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [students]);

  const refreshLinks = useCallback(async () => {
    if (!schoolId) return;
    try {
      const result = await listLinks(schoolId);
      setLinks(result?.links || []);
    } catch (error) {
      console.error('Load class links error:', error);
      showToast({ icon: 'error', title: error?.message || 'Failed to load class links.' });
    }
  }, [listLinks, schoolId]);

  useEffect(() => {
    refreshLinks();
  }, [refreshLinks]);

  const linksByClass = useMemo(() => {
    const map = new Map();
    links.forEach((link) => map.set(link.class_key, link));
    return map;
  }, [links]);

  const buildUrl = (token) => `${window.location.origin}/class-access/${encodeURIComponent(token)}`;

  const handleGenerate = async (classInfo) => {
    try {
      setBusyKey(classInfo.key);
      const result = await generateLink({
        schoolId,
        std_roman: classInfo.std_roman,
        division: classInfo.division,
        custom_div: classInfo.custom_div,
      });

      await refreshLinks();
      if (result?.token) {
        await navigator.clipboard.writeText(buildUrl(result.token));
        showToast({ icon: 'success', title: 'Class link generated and copied.' });
      }
    } catch (error) {
      console.error('Generate class link error:', error);
      showToast({ icon: 'error', title: error?.message || 'Failed to generate link.' });
    } finally {
      setBusyKey('');
    }
  };

  const handleCopy = async (link) => {
    try {
      await navigator.clipboard.writeText(buildUrl(link.token));
      showToast({ icon: 'success', title: 'Login link copied.' });
    } catch (error) {
      showToast({ icon: 'error', title: 'Unable to copy the link.' });
    }
  };

  const handleRegenerate = async (link) => {
    const confirmation = await showConfirmDialog({
      title: 'Regenerate class link?',
      text: 'The existing link will stop working immediately.',
      icon: 'warning',
      confirmButtonText: 'Regenerate',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#2563eb',
    });

    if (!confirmation.isConfirmed) return;

    try {
      setBusyKey(link.class_key);
      const result = await regenerateLink(link.id);
      await refreshLinks();
      if (result?.token) {
        await navigator.clipboard.writeText(buildUrl(result.token));
      }
      showToast({ icon: 'success', title: 'New class link generated and copied.' });
    } catch (error) {
      console.error('Regenerate class link error:', error);
      showToast({ icon: 'error', title: error?.message || 'Failed to regenerate link.' });
    } finally {
      setBusyKey('');
    }
  };

  const handleDisable = async (link) => {
    const confirmation = await showConfirmDialog({
      title: 'Disable class link?',
      text: 'Teachers will no longer be able to open this link.',
      icon: 'warning',
      confirmButtonText: 'Disable',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#dc2626',
    });

    if (!confirmation.isConfirmed) return;

    try {
      setBusyKey(link.class_key);
      await disableLink(link.id);
      await refreshLinks();
      showToast({ icon: 'success', title: 'Class link disabled.' });
    } catch (error) {
      console.error('Disable class link error:', error);
      showToast({ icon: 'error', title: error?.message || 'Failed to disable link.' });
    } finally {
      setBusyKey('');
    }
  };

  return (
    <div className="app-panel class-link-panel">
      <div className="app-panel-header">
        <h3>Unique Class Login Links</h3>
        <p>Share one passwordless link with the teacher responsible for each class.</p>
      </div>

      {loading && !links.length ? (
        <div className="class-access-loading"><Spinner label="Loading class links..." /></div>
      ) : !classes.length ? (
        <div className="class-access-empty">Add students with a standard before generating class links.</div>
      ) : (
        <div className="class-link-list">
          {classes.map((classInfo) => {
            const link = linksByClass.get(classInfo.key);
            const isBusy = busyKey === classInfo.key;

            return (
              <div className="class-link-card" key={classInfo.key}>
                <div className="class-link-card-main">
                  <div className="class-link-icon"><Link2 size={20} /></div>
                  <div>
                    <h4>{classInfo.label}</h4>
                    <p>{classInfo.studentCount} student{classInfo.studentCount === 1 ? '' : 's'}</p>
                  </div>
                </div>

                {link ? (
                  <>
                    <div className="class-link-url" title={buildUrl(link.token)}>
                      {buildUrl(link.token)}
                    </div>
                    <div className="class-link-actions">
                      <button type="button" className="btn btn-primary btn-sm" onClick={() => handleCopy(link)} disabled={isBusy}>
                        <Copy size={15} /> Copy
                      </button>
                      <a className="btn btn-outline-primary btn-sm" href={buildUrl(link.token)} target="_blank" rel="noreferrer">
                        <ExternalLink size={15} /> Open
                      </a>
                      <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => handleRegenerate(link)} disabled={isBusy}>
                        <RefreshCw size={15} /> Regenerate
                      </button>
                      <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => handleDisable(link)} disabled={isBusy}>
                        <ShieldOff size={15} /> Disable
                      </button>
                    </div>
                  </>
                ) : (
                  <button type="button" className="btn btn-primary class-generate-btn" onClick={() => handleGenerate(classInfo)} disabled={isBusy}>
                    {isBusy ? <Spinner label="Generating..." small /> : <><Link2 size={16} /> Generate Login Link</>}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ClassLinkManager;
